import { NextRouter } from 'next/router';
import { Key } from 'react';
import { apiGetAllOrganizations } from '@jove-frontend/uikit';
import { uniqueId } from 'lodash';
import { StateCreator, create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { getAllValidations } from '../../api/validationApi';

export interface ICatalogState<T> {
  collection: T[];
  total_count: number;
  pageSize: number;
  offset: number;
  page: number;
  selectedItems: (string | number | T)[];
  isLoading: boolean;
}

export enum PaginationActionType {
  INITIALIZE = 'initialize',
  NEXT = 'next',
  PREVIOUS = 'previous',
  FIRST = 'first',
  LAST = 'last',
  SIZE = 'size',
}

interface PaginationChangeParams {
  type: PaginationActionType;
  pageSize?: TPageSize;
  page?: number;
  router?: NextRouter;
}

interface ICatalogActions {
  fetch: (firstPage?: boolean) => Promise<unknown>;
  fetchFn: (args: any) => Promise<any>;
  initialize: (fetchFn: (args: any) => Promise<any>) => void;
  setLoading: (bool: boolean) => void;
  handlePaginationChange: (params: PaginationChangeParams) => Promise<void>;
  selectItem: (id: number | string) => void;
  resetSelectedItems: () => void;
}

export interface ICatalogStore<T> {
  catalogState: ICatalogState<T>;
  catalogActions: ICatalogActions;
  resetState: () => void;
}

export type TPageSize = 10 | 25 | 50 | 100;

const initialState: ICatalogState<unknown> = {
  collection: [],
  total_count: null,
  page: 1,
  pageSize: 10,
  offset: 0,
  selectedItems: [],
  isLoading: true,
};

const createCatalogState: StateCreator<
  TCatalogStore<unknown>,
  [['zustand/immer', unknown]],
  [['zustand/immer', never]],
  ICatalogStore<unknown> & Partial<TCatalogStore<unknown>>
> = immer((set, get) => ({
  catalogState: {
    ...initialState,
  },
  catalogActions: {
    fetchFn: null,
    setLoading: (bool) => {
      set((store) => {
        store.catalogState.isLoading = bool;
      });
    },
    initialize: async (fetchFn) => {
      set((store) => {
        store.catalogActions.fetchFn = fetchFn;
      });
      const { fetch } = get().catalogActions;

      await fetch();
    },
    fetch: async (firstPage) => {
      const { fetchFn, setLoading } = get().catalogActions;

      if (!fetchFn) {
        return;
      }

      setLoading(true);

      if (firstPage) {
        set((store) => {
          store.catalogState.offset = 0;
        });
      }

      const params: Parameters<typeof getAllValidations | typeof apiGetAllOrganizations> = [
        {
          limit: get().catalogState.pageSize,
          offset: get().catalogState.offset,
          sortOrder: get().sortState.order,
          sortColumn: get().sortState.column,
          ...get().filterState.filters,
        },
      ];

      const res = await fetchFn(params);

      if (res) {
        const data = res.data || [];
        const total_count = res.total_count || res.totalCount;

        set((store) => {
          store.catalogState.collection = (data || []).map((item) => {
            return {
              ...item,
              uniqueId: uniqueId(),
            };
          });
          store.catalogState.total_count = total_count;
        });
      }

      setLoading(false);
    },
    handlePaginationChange: async ({ type, page: pageParam, pageSize: pageSizeParam, router }: PaginationChangeParams) => {
      set((store) => {
        const { pageSize, total_count } = get().catalogState;

        switch (type) {
          case PaginationActionType.INITIALIZE:
            store.catalogState.pageSize = pageSizeParam;
            store.catalogState.page = pageParam;
            store.catalogState.offset = (pageParam - 1) * pageSizeParam;
            break;

          case PaginationActionType.SIZE:
            store.catalogState.pageSize = pageSizeParam;
            store.catalogState.page = 1;
            store.catalogState.offset = 0;
            break;

          case PaginationActionType.PREVIOUS:
            store.catalogState.offset -= pageSize;
            store.catalogState.page === 1 ? (store.catalogState.page = 1) : (store.catalogState.page -= 1);
            break;

          case PaginationActionType.NEXT:
            store.catalogState.offset += pageSize;
            store.catalogState.page += 1;
            break;

          case PaginationActionType.FIRST:
            store.catalogState.offset = 0;
            store.catalogState.page = 1;
            break;

          case PaginationActionType.LAST:
            const remainder = Math.ceil(total_count % pageSize);
            const offsetRemainder = remainder !== 0 ? remainder : pageSize;
            const newOffset = total_count - offsetRemainder;

            store.catalogState.offset = newOffset;
            store.catalogState.page = Math.ceil(total_count / pageSize);
            break;
        }
      });

      if (router) {
        const routerParams = [{ pageSize: get().catalogState.pageSize }, { page: get().catalogState.page }];
        changeRouter(router, routerParams);
      }

      await get().catalogActions.fetch();
    },
    selectItem: (id) => {
      const { selectedItems } = get().catalogState;
      const isSelected = selectedItems.find((item) => item === id);

      const tmp = isSelected ? selectedItems.filter((item) => item !== id) : [...selectedItems, id];

      set((store) => {
        store.catalogState.selectedItems = [...tmp];
      });
    },
    resetSelectedItems: () => {
      set((store) => {
        store.catalogState.selectedItems = [];
      });
    },
  },
  resetState: () => {
    set((store) => {
      store.catalogState = { ...initialState };
      store.catalogActions.fetchFn = null;
    });
    get().filterActions.resetFilters();
  },
}));

export enum SortOrder {
  asc = 'ASC',
  desc = 'DESC',
}

interface ISortState {
  order: SortOrder;
  column: string;
}

interface ISortActions {
  setSort: (column: string, fetch?: boolean, definedOrder?: SortOrder) => void;
}
interface ISortStore {
  sortState: ISortState;
  sortActions: ISortActions;
}

const createCatalogSortStore: StateCreator<
  TCatalogStore<unknown>,
  [['zustand/immer', unknown]],
  [['zustand/immer', unknown]],
  ISortStore & Partial<TCatalogStore<unknown>>
> = immer((set, get) => ({
  sortState: {
    order: SortOrder.desc,
    column: 'created',
  },
  sortActions: {
    setSort: (column, fetch = true, definedOrder = null) => {
      const prevOrder = get().sortState.order;
      const prevColumn = get().sortState.column;
      let order: SortOrder = definedOrder;

      if (!definedOrder) {
        if (column !== prevColumn || prevOrder === SortOrder.asc) {
          order = SortOrder.desc;
        } else {
          order = SortOrder.asc;
        }
      }

      set((store) => {
        store.sortState = { order, column };
      });

      if (fetch) {
        get().catalogActions.fetch();
      }
    },
  },
}));

/**
 * Filter Store
 */
interface IFilterState {
  filters: any;
  initialState: unknown;
  isInitialized: boolean;
}

interface IFilterActions {
  initialize: (args: Record<Key, Record<Key, any> | string>) => void;
  setFilter: (key: Key, value: Record<Key, unknown> | string) => void;
  clearFilters: () => void;
  resetFilters: () => void;
}

interface IFilterStore {
  filterState: IFilterState;
  filterActions: IFilterActions;
}

interface RouterParams {
  page?: number;
  pageSize?: number;
}

interface Params extends Array<RouterParams> {}

const createCatalogFilterStore: StateCreator<
  TCatalogStore<unknown>,
  [['zustand/immer', unknown]],
  [['zustand/immer', unknown]],
  IFilterStore & Partial<TCatalogStore<unknown>>
> = immer((set, get) => ({
  filterState: {
    filters: null,
    initialState: null,
    isInitialized: false,
  },
  filterActions: {
    initialize: (fields) => {
      if (!get().filterState.isInitialized) {
        set((store) => {
          store.filterState.initialState = fields;
          store.filterState.filters = fields;
          store.filterState.isInitialized = true;
        });
      }
    },
    setFilter: (key, value) => {
      set((store) => {
        if (store.filterState.filters) {
          store.filterState.filters[key] = value;
        }
      });
    },
    clearFilters: () => {
      set((store) => {
        store.filterState.filters = store.filterState.initialState;
      });
    },
    resetFilters: () => {
      set((store) => {
        store.filterState.filters = null;
        store.filterState.initialState = null;
        store.filterState.isInitialized = false;
      });
    },
  },
}));

export type TCatalogStore<T> = ICatalogStore<T> & ISortStore & IFilterStore;

export const createCatalogStoreFactory = <T>() =>
  create<TCatalogStore<T | unknown>>()((...args) => ({
    ...createCatalogState(...args),
    ...createCatalogSortStore(...args),
    ...createCatalogFilterStore(...args),
  }));

function changeRouter(router: NextRouter, params: Params) {
  const searchParams = new URLSearchParams();

  params.forEach((param) => {
    Object.entries(param).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
  });

  router.push({
    pathname: router.pathname,
    query: {
      ...router.query,
      ...Object.fromEntries(searchParams.entries()),
    },
  });
}
